import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../AuthContext';
import '../Dashboard.css';
import '../../components/MealTile.css';
import welcomePhoto from '../../assets/welcomePhoto.png';
import MealCardList from '../../components/MealCardList';

const MealsHosted = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [hostedMeals, setHostedMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMeals, setExpandedMeals] = useState({});

  const toggleMealExpanded = (mealId) => {
    setExpandedMeals(prev => ({
      ...prev,
      [mealId]: !prev[mealId]
    }));
  };

  useEffect(() => {
    const fetchHostedMeals = async () => {
      if (!currentUser) return;

      try {
        const now = new Date();
        const mealsQuery = query(
          collection(db, 'meals'),
          where('hostId', '==', currentUser.uid),
          where('status', '==', 'active'),
          where('dateTime', '<=', now),
          orderBy('dateTime', 'desc')
        );
        const mealsSnapshot = await getDocs(mealsQuery);

        const pastMeals = mealsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Fetch existing reviews by this host
        const reviewsQuery = query(
          collection(db, 'reviews'),
          where('hostId', '==', currentUser.uid),
          where('reviewerRole', '==', 'host')
        );
        const reviewsSnapshot = await getDocs(reviewsQuery);
        const reviewedBookingIds = new Set(
          reviewsSnapshot.docs.map(doc => doc.data().bookingId)
        );

        // Fetch guests for each meal
        const mealsWithGuests = await Promise.all(
          pastMeals.map(async (meal) => {
            const bookingsQuery = query(
              collection(db, 'bookings'),
              where('mealId', '==', meal.id),
              where('hostId', '==', currentUser.uid),
              where('userAttendedMeal', '==', true)
            );
            const bookingsSnapshot = await getDocs(bookingsQuery);

            // Guest name/photo are denormalized onto the booking doc at
            // booking time (F-13), so no per-guest users/{guestId} read is
            // needed here.
            const guests = bookingsSnapshot.docs.map((bookingDoc) => {
              const booking = bookingDoc.data();
              return {
                bookingId: bookingDoc.id,
                guestId: booking.guestId,
                hasReviewed: reviewedBookingIds.has(bookingDoc.id),
                displayName: booking.guestDisplayName ||
                  `${booking.guestFirstName || ''} ${booking.guestLastName || ''}`.trim() ||
                  'Guest',
                profileImage: booking.guestProfileImage,
              };
            });

            return { ...meal, guests };
          })
        );

        setHostedMeals(mealsWithGuests);
      } catch (err) {
        console.error('Error fetching hosted meals:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHostedMeals();
  }, [currentUser]);

  if (!currentUser || loading) {
    return (
      <div className="dashboard-container">
        <p className="dashboard-loading">Loading...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <img
        src={welcomePhoto}
        alt="Welcome"
        className="dashboard-image"
      />

      <div className="dashboard-card">
        <div className="meals-tile">
          <h2 className="meals-tile-title">Meals Hosted</h2>

          <MealCardList
            meals={hostedMeals}
            onSelect={(meal) => navigate(`/meal-summary/${meal.id}`)}
            emptyMessage="You haven't hosted any meals yet."
            renderCardExtra={(meal) => (
              <p className="meal-card-guests">{meal.currentGuests || 0} of {meal.maxGuests} guests</p>
            )}
            renderFooter={(meal) => {
              if (!meal.guests || meal.guests.length === 0) return null;
              const isExpanded = expandedMeals[meal.id];
              return (
                <>
                  <button
                    className="toggle-guests-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMealExpanded(meal.id);
                    }}
                  >
                    {isExpanded ? 'Hide Guests' : `Review Guests (${meal.guests.length})`}
                  </button>
                  {isExpanded && (
                    <div className="guest-list">
                      {meal.guests.map((guest) => (
                        <div key={guest.bookingId} className="guest-item">
                          <div className="guest-info">
                            <img
                              src={guest.profileImage || '/default-avatar.png'}
                              alt={guest.displayName}
                              className="guest-avatar"
                            />
                            <span className="guest-name">{guest.displayName}</span>
                          </div>
                          <button
                            className={`review-link-button ${guest.hasReviewed ? 'reviewed' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!guest.hasReviewed) {
                                navigate(`/create-review/${guest.bookingId}`);
                              }
                            }}
                            disabled={guest.hasReviewed}
                          >
                            {guest.hasReviewed ? 'Reviewed' : 'Review'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            }}
          />
        </div>

        <button className="create-listing-button" onClick={() => navigate('/create-listing')}>
          Host Another Meal
        </button>
        <button className="create-listing-button secondary" onClick={() => navigate('/reviews')}>
          See Reviews
        </button>
      </div>
    </div>
  );
};

export default MealsHosted;
